/**
 * Reusable table component for bulk management of items (keywords, assets, etc.)
 * Provides checkboxes, bulk actions (enable/pause/remove), and status display
 */
import React, { useState } from 'react';
import { KebabMenu } from '@/components/ui/KebabMenu';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { getStatusBadgeClasses } from '@/utils/statusStyles';
import { formatEnumPretty } from '@/utils/formatEnum';

export interface ManagedItem {
  id: string;
  text: string;
  status?: 'ENABLED' | 'PAUSED' | 'REMOVED';
  [key: string]: unknown; // Allow additional properties
}

interface BulkManagementTableProps<T extends ManagedItem> {
  items: T[];
  selectedItems: Set<string>;
  onToggleItem: (id: string) => void;
  onSelectAll: () => void;
  onBulkAction: (action: 'enable' | 'pause' | 'remove', itemIds: string[]) => Promise<void>;
  itemLabel: string; // e.g., "keyword", "asset", "sitelink"
  columns?: {
    header: string | number | React.ReactElement | null;
    key: keyof T | string;
    render?: (item: T) => React.ReactNode;
  }[];
  showStatus?: boolean;
  allowedActions?: ('enable' | 'pause' | 'remove')[];
  className?: string;
  maxVisibleItems?: number; // Enable vertical scroll when items exceed this count (default: 10)
}

export function BulkManagementTable<T extends ManagedItem>({
  items,
  selectedItems,
  onToggleItem,
  onSelectAll,
  onBulkAction,
  itemLabel,
  columns,
  showStatus = true,
  allowedActions = ['enable', 'pause', 'remove'],
  className = '',
  maxVisibleItems = 10,
}: BulkManagementTableProps<T>) {
  const [batchAction, setBatchAction] = useState<'enable' | 'pause' | 'remove' | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const handleBatchActionClick = (action: 'enable' | 'pause' | 'remove') => {
    if (selectedItems.size === 0) return;
    setBatchAction(action);
  };

  const handleExecuteBatchAction = React.useCallback(async () => {
    if (!batchAction) return;

    setIsExecuting(true);
    try {
      await onBulkAction(batchAction, Array.from(selectedItems));
      setBatchAction(null);
    } catch (error) {
      console.error('Batch action error:', error);
    } finally {
      setIsExecuting(false);
    }
  }, [batchAction, onBulkAction, selectedItems]);

  const dialogContent = React.useMemo(() => {
    if (!batchAction) return null;

    const count = selectedItems.size;
    const actionName = batchAction === 'enable' ? 'enable' : batchAction === 'pause' ? 'pause' : 'remove';

    return {
      title: `${actionName.charAt(0).toUpperCase() + actionName.slice(1)} ${itemLabel}s`,
      message: `Are you sure you want to ${actionName} ${count} selected ${itemLabel}${count > 1 ? 's' : ''}?`,
      confirmText: actionName.charAt(0).toUpperCase() + actionName.slice(1),
      variant: batchAction === 'remove' ? 'danger' : batchAction === 'pause' ? 'warning' : 'info'
    } as const;
  }, [batchAction, selectedItems.size, itemLabel]);

  // Default columns if none provided
  const tableColumns = columns || [
    {
      header: itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1),
      key: 'text',
      render: (item: T) => item.text
    },
    {
      header: 'Length',
      key: 'length',
      render: (item: T) => `${item.text.length} chars`
    }
  ];

  // Add vertical scroll when more items than maxVisibleItems
  const needsScroll = items.length > maxVisibleItems;

  return (
    <>
      {/* Outer container: overflow-visible allows KebabMenu dropdown to render outside
          Inner container: overflow-x-auto enables horizontal scrolling on narrow viewports */}
      <div className={`overflow-visible ${className}`}>
        <div className={`overflow-x-auto ${needsScroll ? 'max-h-[480px] overflow-y-auto' : ''}`}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className={`bg-gray-50 ${needsScroll ? 'sticky top-0 z-10' : ''}`}>
            <tr>
              <th className="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={selectedItems.size === items.length && items.length > 0}
                  onChange={onSelectAll}
                  aria-label="Select all items"
                />
              </th>
              {tableColumns.map((col, idx) => (
                <th
                  key={idx}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {col.header}
                </th>
              ))}
              {showStatus && (
                <th className="pl-4 pr-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  <div className="flex items-center gap-1">
                    <span>Status</span>
                    <KebabMenu
                      options={[
                        ...(allowedActions.includes('enable') ? [{
                          label: 'Enable',
                          onClick: () => handleBatchActionClick('enable'),
                          variant: 'success' as const,
                          disabled: selectedItems.size === 0
                        }] : []),
                        ...(allowedActions.includes('pause') ? [{
                          label: 'Pause',
                          onClick: () => handleBatchActionClick('pause'),
                          disabled: selectedItems.size === 0
                        }] : []),
                        ...(allowedActions.includes('remove') ? [{
                          label: 'Remove',
                          onClick: () => handleBatchActionClick('remove'),
                          variant: 'danger' as const,
                          disabled: selectedItems.size === 0
                        }] : [])
                      ]}
                      ariaLabel={`Bulk actions for selected ${itemLabel}s`}
                    />
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item, index) => (
              <tr
                key={item.id || `item-${index}`}
                className={selectedItems.has(item.id) ? 'bg-blue-50' : ''}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={selectedItems.has(item.id)}
                    onChange={() => onToggleItem(item.id)}
                    aria-label={`Select ${item.text}`}
                  />
                </td>
                {tableColumns.map((col, idx) => (
                  <td key={idx} className="px-4 py-3 text-sm text-gray-900">
                    {col.render ? col.render(item) : String(item[col.key as keyof T])}
                  </td>
                ))}
                {showStatus && (
                  <td className="pl-4 pr-2 py-3 text-sm">
                    {item.status && (
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusBadgeClasses(item.status)}`}>
                        {formatEnumPretty(item.status)}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Batch Action Confirmation Dialog */}
      {batchAction && dialogContent && (
        <ConfirmDialog
          key={batchAction}
          isOpen={true}
          onClose={() => setBatchAction(null)}
          onConfirm={handleExecuteBatchAction}
          title={dialogContent.title}
          message={dialogContent.message}
          confirmText={dialogContent.confirmText}
          cancelText="Cancel"
          variant={dialogContent.variant}
          isLoading={isExecuting}
        />
      )}
    </>
  );
}
